<template>
    <v-menu offset-y>
        <template v-slot:activator="{ props }">
            <v-btn
                class="ml-2"
                size="small"
                icon
                v-bind="props"
                variant="text"
                density="compact"
            >
                <v-icon>
                    {{ sortIcon }}
                </v-icon>
            </v-btn>
        </template>
        <v-list>
            <v-list-item @click="handleSortChange('A-Z')">
                <v-list-item-title>A-Z</v-list-item-title>
            </v-list-item>
            <v-list-item @click="handleSortChange('Z-A')">
                <v-list-item-title>Z-A</v-list-item-title>
            </v-list-item>
            <v-list-item
                v-if="showCustomOption"
                @click="handleSortChange(customOptionKey)"
            >
                <v-list-item-title>{{ customOptionLabel }}</v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
/**
 * Reusable table sorting menu component.
 * Provides A-Z, Z-A, and a custom option (e.g., "Inactive" or "Importance").
 *
 * @component
 * @example
 * <TableSortMenu
 *   :model-value="currentSort"
 *   custom-option-key="Inactive"
 *   custom-option-label="Inactive"
 *   @update:model-value="handleSortChange"
 * />
 */
export default {
    name: "TableSortMenu",
    props: {
        /**
         * Current sort value ('A-Z', 'Z-A', or custom option key).
         * @type {String}
         */
        modelValue: {
            type: String,
            default: "A-Z",
        },
        /**
         * Key for the custom option (e.g., 'Inactive' or 'Importance').
         * @type {String}
         */
        customOptionKey: {
            type: String,
            default: "Inactive",
        },
        /**
         * Display label for the custom option.
         * @type {String}
         */
        customOptionLabel: {
            type: String,
            default: "Inactive",
        },
        /**
         * Whether to show the custom option in the menu.
         * @type {Boolean}
         */
        showCustomOption: {
            type: Boolean,
            default: true,
        },
    },
    emits: ["update:modelValue"],
    computed: {
        /**
         * Returns the icon to display based on current sort value.
         * @returns {String} Material Design icon name.
         */
        sortIcon() {
            switch (this.modelValue) {
                case "A-Z":
                    return "mdi-sort-alphabetical-ascending";
                case "Z-A":
                    return "mdi-sort-alphabetical-descending";
                case this.customOptionKey:
                    if (this.customOptionKey === "Importance") {
                        return "mdi-exclamation";
                    }
                    // Default to mdi-account-off for 'Inactive'
                    return "mdi-account-off";
                default:
                    return "mdi-sort-alphabetical-ascending";
            }
        },
    },
    methods: {
        /**
         * Handles sort option selection and emits update event.
         * @param {String} value - The selected sort option.
         */
        handleSortChange(value) {
            this.$emit("update:modelValue", value);
        },
    },
};
</script>
